/**
 * EU-711 — o store que liga o detector ao navegador e à UI.
 *
 * Responsabilidades: escutar `online`/`offline`, empurrar a entrada de histórico
 * que faz o "voltar" do navegador funcionar (OFF-005), guardar a ação que
 * "Tentar novamente" re-executa (OFF-003) e expor um snapshot estável para o
 * `useSyncExternalStore`.
 *
 * A entrada de histórico usa a MESMA URL de propósito: a tela de sem-internet
 * não vive numa rota. Se vivesse, (a) o `useBlocker` da etapa SurveyJS
 * interceptaria com "Tem certeza? Se sair agora, as informações preenchidas não
 * serão enviadas.", (b) a `ModulePage` desmontaria e o preenchimento morreria, e
 * (c) um F5 recarregaria a tela de ERRO em vez da etapa (OFF-010). Com a URL
 * intacta, o "voltar" fecha a tela e o F5 volta à etapa.
 */
import { isNetworkFailure } from './is-network-failure';
import {
  createOfflineDetector,
  type OfflineDetector,
  type OfflineDetectorDeps,
} from './offline-detector';

export interface OfflineSnapshot {
  offline: boolean;
  /** "Tentar novamente": re-executa a ação/carregamento que falhou. */
  retry: () => Promise<void>;
}

export interface OfflineStoreOptions {
  /**
   * Fallback de "Tentar novamente" quando nenhuma tela registrou uma ação —
   * OFF-012 (listagem, navegação, perfil): recarrega os dados ativos. Injetado
   * pelo `main.tsx` com o `queryClient`, para o store não conhecer o
   * react-query.
   */
  reloadActiveData?: () => Promise<void> | void;
  /** Injeção do relógio — só o teste passa. */
  detectorDeps?: Partial<Pick<OfflineDetectorDeps, 'now' | 'schedule' | 'cancel'>>;
}

export interface OfflineStore {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => OfflineSnapshot;
  /** Registra a ação a re-executar. Devolve o de-registro. */
  registerRetry: (action: () => Promise<void> | void) => () => void;
  /** Fallback de "Tentar novamente" (OFF-012), injetado no boot. */
  setReloadActiveData: (fn: () => Promise<void> | void) => void;
  reportRequestStart: (requestId: number, options?: { watchdog?: boolean }) => void;
  reportRequestSuccess: (requestId: number) => void;
  reportNetworkError: (requestId: number, options?: { watchdog?: boolean }) => void;
  reportRequestSettled: (requestId: number) => void;
  start: () => void;
  stop: () => void;
}

/** Marca nossa entrada de histórico — para nunca engolir um popstate do router. */
const HISTORY_MARKER = '__eurecaOfflineScreen';

export function createOfflineStore(options: OfflineStoreOptions = {}): OfflineStore {
  const listeners = new Set<() => void>();
  let retryAction: (() => Promise<void> | void) | null = null;
  /** `true` entre o push da nossa entrada e o popstate que a consome. */
  let historyEntryPushed = false;
  let started = false;
  let reloadActiveData = options.reloadActiveData ?? null;

  let snapshot: OfflineSnapshot = { offline: false, retry: runRetry };

  function emit(): void {
    for (const listener of [...listeners]) listener();
  }

  /**
   * Reconstrói o snapshot só na transição — `useSyncExternalStore` exige que
   * leituras consecutivas devolvam a MESMA referência, ou o React entra em loop
   * de re-render.
   */
  function applyOffline(offline: boolean): void {
    if (offline) pushHistoryEntry();
    else consumeHistoryEntry();
    snapshot = { offline, retry: runRetry };
    emit();
  }

  function pushHistoryEntry(): void {
    // Idempotente (OFF-011): N sinais de offline → UMA entrada. Sem isso, três
    // cliques no botão de conclusão sem conexão exigiriam três "voltar".
    if (historyEntryPushed || typeof window === 'undefined') return;
    try {
      // MESMA URL, `history.state` do router preservado: o TanStack Router
      // guarda `key`/`index` ali e ficaria inconsistente se sobrescrevêssemos.
      window.history.pushState(
        { ...(window.history.state ?? {}), [HISTORY_MARKER]: true },
        '',
        window.location.href
      );
      historyEntryPushed = true;
    } catch {
      // Sem histórico disponível a tela ainda aparece — só o "voltar" do
      // navegador deixa de fechá-la. Degradar é melhor que quebrar.
      historyEntryPushed = false;
    }
  }

  /**
   * Consome a entrada que empurramos (saída pelo botão, não pelo "voltar").
   *
   * A flag NÃO é zerada aqui de propósito: o `history.back()` gera um `popstate`
   * e é o `handlePopState` que o consome — só assim o evento é interceptado
   * (`stopImmediatePropagation`) antes de chegar ao TanStack Router. Zerar antes
   * deixava esse popstate escapar para o router, que o trataria como
   * back-navigation e acionaria o `useBlocker` da etapa: o diálogo "Tem certeza?"
   * de volta, exatamente o que esta arquitetura existe para evitar.
   */
  function consumeHistoryEntry(): void {
    if (!historyEntryPushed || typeof window === 'undefined') return;
    try {
      window.history.back();
    } catch {
      // Sem histórico para voltar, a flag ficaria presa e o gate passaria a
      // engolir popstate do router. Solta na mão.
      historyEntryPushed = false;
    }
  }

  async function runRetry(): Promise<void> {
    // OFF-004 (caminho curto) — o sistema ainda diz que não há rede: re-tentar
    // produziria a mesma falha e faria a árvore piscar no meio. O candidato
    // permanece na tela, que é exatamente o que o card pede.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

    const action = retryAction ?? reloadActiveData;
    // Fecha ANTES de re-tentar: a árvore precisa estar visível para o
    // carregamento acontecer nela. Falhando de novo, o bloco abaixo reabre.
    detector.clear();
    if (!action) return;
    try {
      await action();
    } catch (err) {
      // OFF-004 (portal cativo) — `navigator.onLine` diz que há rede, mas a
      // re-tentativa caiu por conexão. Volta IMEDIATAMENTE para a tela: a janela
      // de graça do detector existe para não rotear cedo demais na PRIMEIRA
      // falha, não aqui, onde o candidato pediu a re-tentativa explicitamente.
      if (isNetworkFailure(err)) detector.reportSystemOffline();
      // Erro de outra natureza é da tela que registrou a ação — não é nosso.
    }
  }

  const handleOffline = () => detector.reportSystemOffline();
  const handleOnline = () => detector.reportSystemOnline();

  /**
   * O "voltar" do navegador fecha a tela (OFF-005). Com `stopImmediatePropagation`
   * — mas SÓ quando a entrada é a nossa: o router escuta `popstate` na mesma
   * window, e deixá-lo processar um popstate que não muda URL nenhuma é o
   * caminho para ele tratar isso como back-navigation e acionar o `useBlocker`
   * da etapa, trazendo de volta o diálogo "Tem certeza?" que esta arquitetura
   * existe para evitar. Quando a entrada NÃO é nossa, não tocamos no evento.
   *
   * EU-762 (QA 01/10) — a flag `capture: true` NÃO garante a precedência: os
   * dois listeners estão no mesmo alvo, e no alvo o navegador os dispara na
   * ORDEM DE REGISTRO (medido no Chromium 145). O que garante que este handler
   * corre antes do router é o `start()` acontecer antes do `createBrowserHistory`
   * — ver `start-before-router.ts`, importado em primeiro lugar por
   * `router/router.tsx`.
   */
  const handlePopState = (event: PopStateEvent) => {
    if (!historyEntryPushed) return;
    historyEntryPushed = false;
    event.stopImmediatePropagation();
    // No-op quando a tela já fechou (o `consumeHistoryEntry` deste mesmo ciclo
    // é quem gerou o popstate); fecha de fato quando o "voltar" foi do candidato.
    detector.clear();
  };

  // Declarado DEPOIS das funções acima de propósito: `applyOffline` é a callback
  // de transição do detector e o detector é lido pelas funções — as declarações
  // de função são hoisted, e nenhuma delas roda antes desta linha.
  const detector: OfflineDetector = createOfflineDetector({
    now: options.detectorDeps?.now ?? (() => Date.now()),
    schedule:
      options.detectorDeps?.schedule ?? ((fn, delayMs) => globalThis.setTimeout(fn, delayMs)),
    cancel:
      options.detectorDeps?.cancel ??
      ((handle) => globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>)),
    onChange: applyOffline,
  });

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    getSnapshot: () => snapshot,

    registerRetry(action) {
      retryAction = action;
      return () => {
        if (retryAction === action) retryAction = null;
      };
    },

    setReloadActiveData(fn) {
      reloadActiveData = fn;
    },

    reportRequestStart: (requestId, opts) => detector.reportRequestStart(requestId, opts),
    reportRequestSuccess: (requestId) => detector.reportRequestSuccess(requestId),
    reportNetworkError: (requestId, opts) => detector.reportNetworkError(requestId, opts),
    reportRequestSettled: (requestId) => detector.reportRequestSettled(requestId),

    start() {
      if (started || typeof window === 'undefined') return;
      started = true;
      window.addEventListener('offline', handleOffline);
      window.addEventListener('online', handleOnline);
      window.addEventListener('popstate', handlePopState, true);
      // O app pode BOOTAR offline (candidato abre o app já sem rede) — nesse
      // caso não haverá evento nenhum para escutar.
      if (navigator.onLine === false) detector.reportSystemOffline();
    },

    stop() {
      if (!started || typeof window === 'undefined') return;
      started = false;
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('popstate', handlePopState, true);
      historyEntryPushed = false;
      detector.dispose();
    },
  };
}

/** Store do app. O teste cria instâncias próprias. */
export const offlineStore = createOfflineStore();
